import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Button from "@material-ui/core/Button";
import useErrorMessage from "../../../hooks/useErrorMessage";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import {Close, Search} from "@material-ui/icons";
import {containedButton, textButton} from "../../../utils/styles";
import clsx from "clsx";
import {commaSeparate} from "../../../utils";

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'flex-start'
    },
    inputFile: {
        display: 'none'
    },
    formControl: {
        marginRight: theme.spacing()
    },
    widthButton: {
        width: '140px !important'
    },
    errorButton: {
        marginBottom: '24px !important'
    },
    marginTopButton: {
        marginTop: 10
    },
    mobileIconOnly: {
        [theme.breakpoints.down('sm')]: {
            minWidth: theme.spacing(5),
            width: 'auto !important'
        }
    },
    mobileButtonText: {
        [theme.breakpoints.down('sm')]: {
            display: 'none'
        }
    },
    ...containedButton(theme, false, false),
    ...textButton(theme, false)
}));

const getValidFormats = validFormats => commaSeparate(validFormats.map(format => format.format));

/* Tamanho máximo por extenso, ex.: 5 MB, para a mensagem de erro usar o limite de verdade. */
const formatarTamanhoMB = bytes => {
    const mb = bytes / (1024 * 1024);
    return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
};

const isPdfExtension = nome => typeof nome === 'string' && /\.pdf$/i.test(nome);

const CABECALHO_PDF = [0x25, 0x50, 0x44, 0x46]; // %PDF

/*
 * Confere os 4 primeiros bytes do arquivo. Usado só para o tipo ambíguo
 * (application/octet-stream ou vazio, como o Android manda PDF às vezes):
 * sem isso, esse tipo passaria para qualquer arquivo, não só PDF.
 */
const temCabecalhoPdf = async file => {
    try {
        const buffer = await file.slice(0, 4).arrayBuffer();
        const bytes = new Uint8Array(buffer);
        return CABECALHO_PDF.every((byte, index) => bytes[index] === byte);
    } catch (e) {
        return false;
    }
};

export default function FileSelector(props) {
    const {
        variant = 'inputAndButton',
        onSelect,
        onRemove,
        onFile = null,
        error,
        errorMessage,
        dense = false,
        maxSize = 10 * 1024 * 1024,
        validFormats = [
            {format: 'PDF', mimeType: ['application/pdf', 'application/x-download', 'application/octet-stream']}
        ],
        buttonText = 'Selecionar',
        buttonIcon : ButtonIcon = Search,
        buttonVariant = 'contained',
        htmlFor = 'fileType',
        hideButtonTextOnMobile = false,
    } = props;
    const classes = useStyles();
    const showErrorMessage = useErrorMessage();
    const buttonRef = useRef();
    const [file, setFile] = useState();

    const onSelectFile = async event => {
        // O SyntheticEvent é reciclado pelo React (pool) depois do handler retornar:
        // guarda o nó do input antes do primeiro await, nunca lê `event` depois.
        const inputEl = event.target;
        const selectedFile = Array.from(inputEl.files)[0];

        if (await validateFile(selectedFile)) {
            setFile(selectedFile);

            onSelect && onSelect(selectedFile);

            inputEl.value = '';
        }
    };

    const onRemoveFile = () => {
        setFile(null);

        onRemove && onRemove();
    };

    const onClickInput = event => {
        if (event.target.name === htmlFor) {
            buttonRef.current.click();
        }
    }

    const mensagemFormatoInvalido = () => 'O arquivo deve estar em um formato válido (' + validFormats.reduce((accumulator, value, index) => {
        if (index < validFormats.length - 1) {
            return accumulator + value.format + ', ';
        }

        return accumulator + value.format;
    }, '') + ').';

    const tipoAceitoDiretamente = file => validFormats.some(format => format.mimeType.some(mimeType => file.type === mimeType));

    // Tipo ambíguo (ex.: Android manda PDF como application/octet-stream, ou o navegador não informa nenhum) só entra se o formato aceito incluir octet-stream.
    const aceitaTipoAmbiguo = validFormats.some(format => format.mimeType.includes('application/octet-stream'));
    const isTipoAmbiguo = tipo => tipo === 'application/octet-stream' || tipo === '';

    const validateFile = async file => {
        let valid = true;
        let message = '';

        if (!file || file.size === 0) {
            valid = false;

            message = 'Selecionar um arquivo válido é obrigatório';
        } else if (file.size > maxSize) {
            valid = false;

            message = `O arquivo deve ser menor do que ${formatarTamanhoMB(maxSize)}`;
        } else if (isTipoAmbiguo(file.type) && aceitaTipoAmbiguo) {
            if (!isPdfExtension(file.name) || !(await temCabecalhoPdf(file))) {
                valid = false;

                message = mensagemFormatoInvalido();
            }
        } else if (!tipoAceitoDiretamente(file)) {
            valid = false;

            message = mensagemFormatoInvalido();
        }

        if (!valid) {
            showErrorMessage(message);
        }

        return valid;
    };

    return (
        <div className={classes.root}>
            {variant === 'inputAndButton' && (
                <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.formControl}>
                    <OutlinedInput
                        name={htmlFor}
                        type='text'
                        placeholder='Nenhum arquivo selecionado...'
                        value={!onFile ? (!!file ? file.name : '') : onFile.nome}
                        onClick={onClickInput}
                        endAdornment={!!file &&
                            <InputAdornment position="end">
                                <IconButton
                                    name='removerIconButton'
                                    aria-label="Remover"
                                    edge="end"
                                    onClick={onRemoveFile}
                                    disabled={!file}>
                                    <Close />
                                </IconButton>
                            </InputAdornment>
                        }
                        readOnly
                        error={!!error} />
                    {!!error && (
                        <FormHelperText error>{errorMessage}</FormHelperText>
                    )}
                    {!error && (
                        <FormHelperText>Formatos válidos: {getValidFormats(validFormats)}</FormHelperText>
                    )}
                </FormControl>
            )}
            <input
                id={htmlFor}
                type='file'
                className={classes.inputFile}
                onChange={onSelectFile} />
            <label htmlFor={htmlFor} className={clsx(variant === 'inputAndButton' && classes.marginTopButton)}>
                <Button
                    size={!!dense ? 'small' : 'medium'}
                    ref={buttonRef}
                    variant={buttonVariant}
                    component='span'
                    startIcon={!dense && <ButtonIcon />}
                    className={clsx(!dense && classes.widthButton,
                        hideButtonTextOnMobile && classes.mobileIconOnly,
                        buttonVariant === 'contained' ? classes.containedButton : classes.textButton,
                        !!error && classes.errorButton)}>
                    {!dense ? (
                        <span className={hideButtonTextOnMobile ? classes.mobileButtonText : undefined}>
                            {buttonText}
                        </span>
                    ) : <ButtonIcon />}
                </Button>
            </label>
        </div>
    );
}

FileSelector.propTypes = {
    variant: PropTypes.oneOf([ 'singleButton', 'inputAndButton' ]),
    buttonText: PropTypes.string,
    buttonIcon: PropTypes.elementType,
    buttonVariant: PropTypes.string,
    hideButtonTextOnMobile: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    dense: PropTypes.bool,
    validFormats: PropTypes.array
};
